import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Circle } from "lucide-react";
import { api } from "@/lib/api";

/**
 * TopInfoBar — single premium information bar consolidating the old
 * breaking banner, NFL live ticker and beat-wire ticker.
 * Modes: breaking (pinned live article, red) > default (wire marquee + Titans score chip).
 */
export default function TopInfoBar() {
  const [pinned, setPinned] = useState(null);
  const [titansEv, setTitansEv] = useState(null);
  const [ticker, setTicker] = useState([]);

  useEffect(() => {
    let dead = false;
    const load = async () => {
      try {
        const [p, s, h] = await Promise.allSettled([
          api.get("/live-articles/pinned"),
          api.get("/live-scoreboard"),
          api.get("/home/pulse"),
        ]);
        if (dead) return;
        setPinned(p.status === "fulfilled" ? p.value.data.item || null : null);
        setTitansEv(s.status === "fulfilled" ? s.value.data?.titans_event || null : null);
        if (h.status === "fulfilled") setTicker(h.value.data?.ticker || []);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { dead = true; clearInterval(t); };
  }, []);

  // BREAKING MODE — pinned live article takes over the bar (red = urgent).
  if (pinned) {
    const latest = (pinned.updates && pinned.updates[0]) || null;
    return (
      <Link
        to={`/live/${pinned.id}`}
        data-testid="top-info-bar"
        className="block w-full bg-gradient-to-r from-titans-red to-[#8f0a20] hover:from-titans-red-hover hover:to-titans-red text-white transition-colors"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 h-10 flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 font-cond uppercase tracking-[0.3em] text-[10px] shrink-0">
            <Circle size={7} className="fill-white animate-pulse" /> {pinned.status === "live" ? "Live" : "Just Ended"}
          </span>
          <span className="font-display text-sm uppercase tracking-wide truncate">{pinned.title}</span>
          {latest && <span className="hidden md:block text-white/85 text-xs font-body truncate">{latest.text}</span>}
        </div>
      </Link>
    );
  }

  const gameChip = titansEv && (titansEv.state === "in" || titansEv.state === "pre");
  const isLive = titansEv?.state === "in";
  const items = ticker.length ? ticker : null;

  return (
    <div
      data-testid="top-info-bar"
      className="w-full border-b border-white/[0.08] overflow-hidden"
      style={{ background: "var(--navy-900, #081729)" }}
    >
      <div className="flex items-center h-9">
        <span className="shrink-0 relative z-10 h-full flex items-center bg-titans-red text-white font-cond uppercase tracking-[0.22em] text-[9px] px-3">
          The Wire
        </span>
        {gameChip && (
          <a
            href={`https://www.espn.com/nfl/game/_/gameId/${titansEv.id}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="top-bar-game-chip"
            className={`shrink-0 h-full flex items-center gap-2 px-3 font-cond uppercase tracking-wider text-[11px] ${
              isLive ? "bg-titans-red/20 text-white" : "bg-white/[0.05] text-white/85"
            } border-r border-white/[0.08]`}
          >
            {isLive && <span className="live-dot" style={{ transform: "scale(0.7)" }} />}
            <span>{titansEv.away.abbr} <b className="font-display">{titansEv.away.score ?? "–"}</b></span>
            <span className="text-white/40">·</span>
            <span>{titansEv.home.abbr} <b className="font-display">{titansEv.home.score ?? "–"}</b></span>
            <span className="text-titans-blue text-[9px]">{isLive ? `Q${titansEv.period || ""} ${titansEv.clock || ""}` : titansEv.state_detail || ""}</span>
          </a>
        )}
        <div className="flex-1 min-w-0 overflow-hidden">
          {items ? (
            <div className="ticker-track py-1 pl-6">
              {[...items, ...items].map((it, i) => (
                <span key={i} className="inline-flex items-center gap-2 font-cond uppercase tracking-wider text-[10px] text-white/70 whitespace-nowrap">
                  <span className="text-titans-red text-[6px]">●</span>
                  <span className="text-titans-blue">{it.source}</span> {it.text}
                </span>
              ))}
            </div>
          ) : (
            <span className="pl-6 font-cond uppercase tracking-wider text-[10px] text-white/40">Loading the wire…</span>
          )}
        </div>
      </div>
    </div>
  );
}
